"use client"

import { useState, useEffect } from "react"
import { Lightbulb, Target, Compass } from "lucide-react"
import { useLanguage } from "./LanguageContext"
import { loadAppConfiguration } from "./ContentLoader"
import { useSeo } from "./hooks/useSeo"
import Navigation, { Footer } from "./Navigation"
import "./css/AboutPage.css"

const AboutPage = ({ onReturnHome }) => {
  // Get current language from context
  const { language } = useLanguage()

  // State for app configuration and about content
  const [appConfig, setAppConfig] = useState(null)
  const [aboutContent, setAboutContent] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const bannerImage2 = aboutContent?.banner2?.image

  // Helper function to get localized text from content
  const t = (path, defaultText = "") => {
    // If appConfig isn't loaded yet, return the default text
    if (!appConfig) return defaultText

    // Split the path by dots to navigate nested objects
    const keys = path.split(".")
    let value = appConfig

    // Navigate through the object
    for (const key of keys) {
      if (!value || !value[key]) return defaultText
      value = value[key]
    }

    // Return the localized text if it's an object with language keys
    if (value && typeof value === "object" && value[language.code]) {
      return value[language.code]
    }

    // Return the value if it's a string or the default text
    return typeof value === "string" ? value : value.en || defaultText
  }

  useSeo({
    path: "/about",
    title: language.code === "en" ? "About Techtok" : "Over Techtok",
    description:
      language.code === "en"
        ? "Techtok makes digital literacy fun and accessible for every child. Meet the team and the vision behind the platform."
        : "Techtok maakt digitale geletterdheid leuk en toegankelijk voor ieder kind. Maak kennis met het team en de visie achter het platform.",
  })

  // Load app configuration and about content
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      try {
        // Load the app configuration
        const companyId = new URLSearchParams(window.location.search).get("company") || null
        const config = await loadAppConfiguration(language.code, companyId, false)
        setAppConfig(config)

        // Apply accent color if provided
        if (config?.branding?.accentColor) {
          document.documentElement.style.setProperty("--accent-color", config.branding.accentColor)
        }

        // Load the about content
        const aboutResponse = await fetch(`/content/main/app-about.json`)
        if (!aboutResponse.ok) {
          throw new Error(`Failed to load about content: ${aboutResponse.status}`)
        }
        const aboutData = await aboutResponse.json()
        setAboutContent(aboutData)
      } catch (error) {
        console.error("Failed to load data:", error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [language.code])

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    )
  }

  // Map section icons based on index
  const getSectionIcon = (index) => {
    switch (index) {
      case 0:
        return <Lightbulb />
      case 1:
        return <Target />
      case 2:
        return <Compass />
      default:
        return <Lightbulb />
    }
  }

  // Handle navigation
  const handleNavigation = (page) => {
    if (page === "home") {
      onReturnHome()
    }
  }

  return (
    <div className="about-selector-container">
      <Navigation appConfig={appConfig} currentPage="about" onNavigate={handleNavigation} t={t} />

      {/* About Content */}
      <main className="main-content landing-selector-container">

       {/* Our Story Section */}
       <section className="hero-section-top">
          <div className="max-w-7xl mx-auto relative z-10">
            <div className="grid lg:grid-cols-2 gap-8 items-center px-[40px] py-[0px]">
              {/* Text Content - Left Side */}
              <div className="text-left">
                <h1 className="hero-title">{aboutContent.story.subtitle?.[language.code]}</h1>
                <p className="hero-subtitle">
                    {aboutContent.story.founders.content?.[language.code]}
                </p>
              </div>

              {bannerImage2 && (
                <img
                  src={bannerImage2 || "/placeholder.svg"}
                  alt="Banner"
                  className="mx-auto mb-6 w-full max-w-3xl rounded-2xl"
                  style={{ objectFit: "cover" }}
                />
              )}
            </div>
          </div>

         
        </section>


        {/* Vision Sections */}
        <div className="about-sections about-main">
            {aboutContent?.sections?.map((section, index) => (
              <section key={index} className="about-section">
                <div className="section-icon">{getSectionIcon(index)}</div>
                <h2 className="section-heading">{section.heading?.[language.code] || `Section ${index + 1}`}</h2>
                <p className="section-content">{section.content?.[language.code] || ""}</p>
              </section>
            ))}
          </div>


                {/* Team Section */}
          {aboutContent?.team && (
            <section className="team-main-about">
              <div className="about-me-container">
                <div className="about-me-content" style={{ display: "flex", gap: "2rem", flexWrap: "wrap" }}>
                  {aboutContent.team.map((person, idx) => (
                    <div key={person.title?.[language.code]} style={{ flex: "1 1 320px", maxWidth: 450 }}>
                      <div className="about-me-image-container">
                        <img
                          src={person.image || "/placeholder.svg"}
                          alt={person.title?.[language.code]}
                          className="about-me-image"
                        />
                      </div>
                      <div className="about-me-text">
                        <h3 className="about-me-title">{person.title?.[language.code]}</h3>
                        <h4 className="about-me-subtitle">{person.subtitle?.[language.code]}</h4>
                        <p className="about-me-description">{person.content?.[language.code]}</p>
                        {person.linkUrl && (
                          <a href={person.linkUrl} target="_blank" rel="noopener noreferrer" className="about-me-link">
                            {person.linkText?.[language.code]}
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

               


        {/* exita Content */}
        <div className="exit-banner">
      <div className="coming-soon-section">
        <h1 className="hero-title">{aboutContent.contact.heading?.[language.code] || "Get in Touch"}</h1>
        <p className="hero-subtitle">
        {t(
              "comingSoon.contact",
              "We're crafting exciting new lessons to help you unlock even more digital skills. Stay tuned for updates—you won't want to miss what's next!",
            )}


          </p>

          <button className="main-action-button">
            <span className="language-flag">
              Email: <a href={`mailto:${aboutContent.contact.email}`}>{aboutContent.contact.email}</a>
            </span>
          </button>
        </div>

        


        </div>
      </main>

      <Footer appConfig={appConfig} t={t} />
    </div>
  )
}

export default AboutPage
